import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getStructureTypes,
  createStructureType,
  deleteStructureType,
  getStructureTasks,
  createStructureTask,
  patchStructureTask,
  deleteStructureTask,
  getStructureTaskModules,
  createStructureTaskModule,
  patchStructureTaskModule,
  deleteStructureTaskModule,
  getVideoSignedUploadUrl,
  uploadVideoToGCS,
  markStructureTaskModuleWatched,
  markStructureTaskModuleCompleteForUser,
  unmarkStructureTaskModuleCompleteForUser,
} from '../api';
import { StructureType, StructureTask, StructureTaskModule } from '../types';

export const trainingKeys = {
  all: ['training'] as const,
  structureTypes: () => [...trainingKeys.all, 'structure-types'] as const,
  structureTasks: (structureTypeId?: string) =>
    [...trainingKeys.all, 'structure-tasks', structureTypeId] as const,
  modules: (taskId?: string) => [...trainingKeys.all, 'modules', taskId] as const,
};

const unwrapList = <T>(data: T[] | { results: T[] }): T[] =>
  Array.isArray(data) ? data : (data?.results ?? []);

export function useStructureTypes() {
  return useQuery<StructureType[]>({
    queryKey: trainingKeys.structureTypes(),
    queryFn: async () => {
      const response = await getStructureTypes();
      return unwrapList<StructureType>(response.data);
    },
  });
}

export function useStructureTasks(structureTypeId?: string) {
  return useQuery<StructureTask[]>({
    queryKey: trainingKeys.structureTasks(structureTypeId),
    queryFn: async () => {
      const response = await getStructureTasks(structureTypeId);
      return unwrapList<StructureTask>(response.data);
    },
  });
}

export function useStructureTaskModules(taskId?: string) {
  return useQuery<StructureTaskModule[]>({
    queryKey: trainingKeys.modules(taskId),
    queryFn: async () => {
      const response = await getStructureTaskModules(taskId);
      return unwrapList<StructureTaskModule>(response.data);
    },
  });
}

export function useCreateStructureType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; is_preset: boolean }) => createStructureType(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trainingKeys.structureTypes() });
    },
  });
}

export function useDeleteStructureType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteStructureType(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trainingKeys.structureTypes() });
    },
  });
}

export function useCreateStructureTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; structure_type: string }) => createStructureTask(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trainingKeys.all });
    },
  });
}

export function useDeleteStructureTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteStructureTask(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trainingKeys.all });
    },
  });
}

export function useUpdateStructureTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name: string; structure_type: string } }) =>
      patchStructureTask(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trainingKeys.all });
    },
  });
}

export function useCreateStructureTaskModule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      name,
      description,
      task,
      video,
      required_user_ids,
      onProgress,
    }: {
      name: string;
      description: string;
      task: string;
      video: File;
      required_user_ids?: number[];
      onProgress?: (percent: number) => void;
    }) => {
      // Step 1: get a signed PUT URL from our backend
      const { data: urlData } = await getVideoSignedUploadUrl({
        file_name: video.name,
        content_type: video.type || 'video/mp4',
        task_id: task,
      });

      // Step 2: upload the file directly to GCS (never hits our server)
      await uploadVideoToGCS(video, urlData.signed_url, onProgress);

      // Step 3: create the database record with the GCS path
      const { data: module } = await createStructureTaskModule({
        name,
        description,
        task,
        video_gcs_path: urlData.gcs_path,
        required_user_ids,
      });

      return module;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trainingKeys.all });
    },
  });
}

export function useUpdateStructureTaskModule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      name,
      description,
      task,
      video,
      required_user_ids,
      onProgress,
    }: {
      id: string;
      name: string;
      description: string;
      task: string;
      video?: File | null;
      required_user_ids?: number[];
      onProgress?: (percent: number) => void;
    }) => {
      let video_gcs_path: string | undefined;

      if (video) {
        const { data: urlData } = await getVideoSignedUploadUrl({
          file_name: video.name,
          content_type: video.type || 'video/mp4',
          task_id: task,
        });
        await uploadVideoToGCS(video, urlData.signed_url, onProgress);
        video_gcs_path = urlData.gcs_path;
      }

      const { data: module } = await patchStructureTaskModule(id, {
        name,
        description,
        task,
        required_user_ids,
        ...(video_gcs_path ? { video_gcs_path } : {}),
      });

      return module;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trainingKeys.all });
    },
  });
}

export function useDeleteStructureTaskModule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteStructureTaskModule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trainingKeys.all });
    },
  });
}

export function useMarkStructureTaskModuleWatched() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; currentUserId?: number | null }) =>
      markStructureTaskModuleWatched(id),
    onMutate: async ({ id, currentUserId }) => {
      await queryClient.cancelQueries({ queryKey: trainingKeys.all });
      const previousModules = queryClient.getQueriesData<StructureTaskModule[]>({
        queryKey: trainingKeys.all,
      });

      const optimisticWatchedAt = new Date().toISOString();
      previousModules.forEach(([queryKey, modules]) => {
        if (!modules) return;
        queryClient.setQueryData<StructureTaskModule[]>(
          queryKey,
          modules.map((module) => {
            if (module.id !== id) return module;
            const currentStatus = module.required_watch_status ?? [];
            const updatedStatus =
              currentUserId == null
                ? currentStatus
                : currentStatus.map((status) =>
                    status.user_id === currentUserId
                      ? { ...status, watched: true, watched_at: optimisticWatchedAt }
                      : status,
                  );
            return {
              ...module,
              watched_by_current_user: true,
              watched_at_current_user: optimisticWatchedAt,
              required_watch_status: updatedStatus,
            };
          }),
        );
      });

      return { previousModules };
    },
    onError: (_err, _id, context) => {
      context?.previousModules?.forEach(([queryKey, modules]) => {
        queryClient.setQueryData(queryKey, modules);
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trainingKeys.all });
    },
  });
}

export function useUpdateStructureTaskModuleRequiredUsers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, required_user_ids }: { id: string; required_user_ids: number[] }) =>
      patchStructureTaskModule(id, { required_user_ids }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trainingKeys.all });
    },
  });
}

export function useMarkStructureTaskModuleCompleteForUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: number }) =>
      markStructureTaskModuleCompleteForUser(id, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trainingKeys.all });
    },
  });
}

export function useUnmarkStructureTaskModuleCompleteForUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: number }) =>
      unmarkStructureTaskModuleCompleteForUser(id, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trainingKeys.all });
    },
  });
}
